import React from "react";
import { FormattedMessage } from "react-intl";
import PropTypes from "prop-types";
import DisplayBox from "components/DisplayBox";
import { css } from "@emotion/react";
import { colors, fontSize } from "style";
import { Row } from "components/Containers";
import { getApplicationQuestionnaireItems, renderAssessmentAnswer } from "constants/index";

/**
 * ApplicationQuestionnaireResponses
 *
 * @param {Object} data
 * @param {String} loading
 * @param {Object} assessment
 */
const ApplicationQuestionnaireResponses = ({ data, loading, assessment, ...props }) => {
  const { questions, answers } = assessment || {};

  const items = getApplicationQuestionnaireItems(data, questions).map(({ number, legacyQuestion, question }) =>
    legacyQuestion
      ? {
          number,
          label: <FormattedMessage id={legacyQuestion.titleId} />,
          answer: <FormattedMessage id={legacyQuestion.answerId(data[legacyQuestion.key])} />,
        }
      : {
          number,
          label: (
            <>
              {question.wording} {question.properties?.required ? "" : <OptionalText />}
            </>
          ),
          answer: renderAssessmentAnswer(answers?.[question.id], question) || (
            <FormattedMessage id="Global.NotProvided" />
          ),
        }
  );

  return (
    <DisplayBox
      numColumns={1}
      loading={loading}
      isEmpty={!loading && items.length === 0}
      data={items.map(({ number, label, answer }) => ({
        title: (
          <div css={styles.question(number === 1)}>
            <Row css={styles.question_row}>
              <QuestionNumber index={number} />
              <div css={styles.question_label}>{label}</div>
            </Row>
          </div>
        ),
        body: <div css={styles.body}>{answer}</div>,
      }))}
      hideHeader
      {...props}
    />
  );
};

const QuestionNumber = ({ index }) => (
  <div css={styles.count}>
    <FormattedMessage id="MissionRoleApplicant.QuestionnaireResponseBox.QuestionNumber" values={{ index }} />
  </div>
);

const OptionalText = () => (
  <span css={styles.optionalText}>
    (<FormattedMessage id="Global.Optional" />)
  </span>
);

const styles = {
  body: css`
    font-weight: 400;
    color: ${colors.purpleRainDark2};
    font-size: ${fontSize.normal};
    line-height: normal;
    margin-left: 4rem;
  `,
  question: (isFirst) => css`
    font-weight: 600;
    color: ${colors.purpleRainDark2};
    font-size: ${fontSize.normal};
    margin-left: -3rem;
    padding-left: 3rem;
    width: calc(100% + 6rem);
    padding-right: 3rem;

    ${!isFirst &&
    `
      padding-top: 2rem;
      border-top: 1px solid ${colors.grayAnatomyLight3};
    `}
  `,
  question_row: css`
    align-items: flex-start;
  `,
  question_label: css`
    flex: 1;
  `,
  optionalText: css`
    font-style: italic;
    color: ${colors.grayAnatomyBase2};
    font-size: ${fontSize.xsmall};
    margin-left: 0.25rem;
  `,
  count: css`
    width: 3rem;
    text-align: right;
  `,
};

QuestionNumber.propTypes = {
  index: PropTypes.number.isRequired,
};

ApplicationQuestionnaireResponses.propTypes = {
  data: PropTypes.object.isRequired,
  assessment: PropTypes.object,
  loading: PropTypes.bool,
};

export default ApplicationQuestionnaireResponses;
